import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChurchLogoUploader } from '@/components/shared/church-logo-uploader';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { PermissionGate } from '@/components/shared/permission-gate';
import { PERMISSIONS } from '@/lib/permissions';
import {
  ScriptureRefPicker,
  scriptureRefPickerLabels,
} from '@/components/shared/scripture-ref-picker';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { formatDate, formatDateTime } from '@/utils/format';

interface Devotional {
  id: string;
  title: string;
  devotionalDate: string;
  scriptureRef: string | null;
  body: string;
  prayer: string | null;
  reflectionQuestion: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  audioKey: string | null;
  audioUrl: string | null;
  audioDurationSeconds: number | null;
  notifyOnPublish: boolean;
  status: 'draft' | 'scheduled' | 'published';
  scheduledFor: string | null;
  publishedAt: string | null;
  canDelete?: boolean;
}

const publishDisplay = (row: Devotional): string | null => row.scheduledFor ?? row.publishedAt;

type SubmitAction = 'draft' | 'publish' | 'schedule';

const emptyForm = () => ({
  title: '',
  devotionalDate: null as string | null,
  scriptureRef: '',
  body: '',
  prayer: '',
  reflectionQuestion: '',
  imageKey: null as string | null,
  imagePreviewUrl: null as string | null,
  audioKey: null as string | null,
  audioName: null as string | null,
  audioDurationSeconds: null as number | null,
  notifyOnPublish: false,
  scheduledFor: null as string | null,
});

const audioDurationSeconds = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      const seconds = Number.isFinite(audio.duration) ? Math.round(audio.duration) : null;
      URL.revokeObjectURL(url);
      resolve(seconds);
    });
    audio.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(null);
    });
  });

export const DevotionalsPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Devotional | null>(null);
  const [detail, setDetail] = useState<Devotional | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [audioUploading, setAudioUploading] = useState(false);
  const [pendingAction, setPendingAction] = useState<SubmitAction | null>(null);
  const [publishTarget, setPublishTarget] = useState<Devotional | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<Devotional | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Devotional | null>(null);

  const dateRange = useContentPeriodStore((state) => state.dateRange);

  const list = useQuery({
    queryKey: ['content', 'devotionals', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<{ data: Devotional[] }>('/content/devotionals', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (devotional: Devotional) => {
    setEditing(devotional);
    setForm({
      title: devotional.title,
      devotionalDate: devotional.devotionalDate,
      scriptureRef: devotional.scriptureRef ?? '',
      body: devotional.body,
      prayer: devotional.prayer ?? '',
      reflectionQuestion: devotional.reflectionQuestion ?? '',
      imageKey: devotional.imageKey,
      imagePreviewUrl: devotional.imageUrl,
      audioKey: devotional.audioKey,
      audioName: devotional.audioKey ? t('devotionals.audioAttached') : null,
      audioDurationSeconds: devotional.audioDurationSeconds,
      notifyOnPublish: devotional.notifyOnPublish,
      scheduledFor: devotional.scheduledFor,
    });
    setDetail(null);
    setFormOpen(true);
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    devotionalDate: form.devotionalDate,
    scriptureRef: form.scriptureRef.trim() || null,
    body: form.body.trim(),
    prayer: form.prayer.trim() || null,
    reflectionQuestion: form.reflectionQuestion.trim() || null,
    imageKey: form.imageKey,
    audioKey: form.audioKey,
    audioDurationSeconds: form.audioDurationSeconds,
    notifyOnPublish: form.notifyOnPublish,
  });

  const save = useMutation({
    mutationFn: async (action: SubmitAction) => {
      const body = buildPayload();
      let devotional = editing
        ? await api.patch<Devotional>(`/content/devotionals/${editing.id}`, body)
        : await api.post<Devotional>('/content/devotionals', body);

      if (action === 'publish') {
        devotional = await api.post<Devotional>(`/content/devotionals/${devotional.id}/publish`);
      } else if (action === 'schedule') {
        devotional = await api.post<Devotional>(`/content/devotionals/${devotional.id}/schedule`, {
          scheduledFor: form.scheduledFor,
        });
      }

      return { action, devotional };
    },
    onSuccess: ({ action, devotional }) => {
      toast.success(
        action === 'publish'
          ? t('devotionals.published')
          : action === 'schedule'
            ? t('devotionals.scheduled')
            : t('devotionals.saved'),
      );
      setFormOpen(false);
      resetForm();
      setDetail(devotional);
      setPendingAction(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'devotionals'] });
    },
    onError: (err) => {
      setPendingAction(null);
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const submit = (action: SubmitAction) => {
    setPendingAction(action);
    save.mutate(action);
  };

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Devotional>(`/content/devotionals/${id}/publish`),
    onSuccess: (devotional) => {
      toast.success(t('devotionals.published'));
      setPublishTarget(null);
      setDetail(devotional);
      void queryClient.invalidateQueries({ queryKey: ['content', 'devotionals'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Devotional>(`/content/devotionals/${id}/unpublish`),
    onSuccess: (devotional) => {
      toast.success(t('devotionals.unpublished'));
      setUnpublishTarget(null);
      setDetail(devotional);
      void queryClient.invalidateQueries({ queryKey: ['content', 'devotionals'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/devotionals/${id}`),
    onSuccess: () => {
      toast.success(t('devotionals.deleted'));
      setDeleteTarget(null);
      setDetail(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'devotionals'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Devotional>[] = [
    {
      id: 'date',
      header: t('devotionals.columns.date'),
      cell: (row) => formatDate(row.devotionalDate),
    },
    {
      id: 'publishAt',
      header: t('devotionals.columns.publishAt'),
      cell: (row) => {
        const value = publishDisplay(row);
        return value ? formatDateTime(value) : '—';
      },
    },
    {
      id: 'title',
      header: t('devotionals.columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'status',
      header: t('devotionals.columns.status'),
      cell: (row) => (
        <Badge
          variant={
            row.status === 'published'
              ? 'success'
              : row.status === 'scheduled'
                ? 'info'
                : 'neutral'
          }
        >
          {t(`status.${row.status}`)}
        </Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];

  const scheduleInPast =
    form.scheduledFor !== null && new Date(form.scheduledFor).getTime() <= Date.now();

  const canSubmit =
    form.title.trim().length > 0 &&
    form.devotionalDate !== null &&
    form.body.trim().length > 0 &&
    !save.isPending &&
    !audioUploading;

  const canSchedule = canSubmit && form.scheduledFor !== null && !scheduleInPast;
  const canPublish = canSubmit;
  const canSaveDraft = canSubmit;

  const editingPublished = editing?.status === 'published';
  const detailShowPublish = detail?.status === 'draft' || detail?.status === 'scheduled';

  return (
    <div>
      <ContentPageHeader
        actions={
          <PermissionGate permission={PERMISSIONS.CONTENT_CREATE}>
            <Button onClick={openCreate}>{t('devotionals.create')}</Button>
          </PermissionGate>
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('devotionals.empty')}</p>}
        onRowClick={(row) => setDetail(row)}
        rowActions={(row) => (
          <div className="flex justify-end gap-2">
            {row.status === 'published' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setUnpublishTarget(row)}
              >
                {t('devotionals.unpublish')}
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPublishTarget(row)}>
                {t('devotionals.publish')}
              </Button>
            )}
            {row.canDelete === true ? (
              <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                <Button size="sm" variant="outline" onClick={() => setDeleteTarget(row)}>
                  {tCommon('action.delete')}
                </Button>
              </PermissionGate>
            ) : null}
          </div>
        )}
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{detail?.title ?? t('devotionals.title')}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <>
              <DialogBody>
                <div className="flex items-start gap-4">
                  {detail.imageUrl ? (
                    <img
                      src={detail.imageUrl}
                      alt={t('devotionals.artworkAlt', { title: detail.title })}
                      className="size-20 shrink-0 rounded-lg object-cover"
                    />
                  ) : null}
                  <div className="min-w-0 flex-1 space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={
                          detail.status === 'published'
                            ? 'success'
                            : detail.status === 'scheduled'
                              ? 'info'
                              : 'neutral'
                        }
                      >
                        {t(`status.${detail.status}`)}
                      </Badge>
                    </div>
                    <dl className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-caption text-fg-secondary">
                          {t('devotionals.columns.date')}
                        </dt>
                        <dd className="text-body-sm">{formatDate(detail.devotionalDate)}</dd>
                      </div>
                      <div>
                        <dt className="text-caption text-fg-secondary">
                          {t('devotionals.columns.publishAt')}
                        </dt>
                        <dd className="text-body-sm">
                          {publishDisplay(detail) ? formatDateTime(publishDisplay(detail)!) : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-caption text-fg-secondary">
                          {t('devotionals.scriptureLabel')}
                        </dt>
                        <dd className="text-body-sm">{detail.scriptureRef ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-caption text-fg-secondary">
                          {t('devotionals.notifyLabel')}
                        </dt>
                        <dd className="text-body-sm">
                          {detail.notifyOnPublish ? tCommon('boolean.yes') : tCommon('boolean.no')}
                        </dd>
                      </div>
                    </dl>
                    <div>
                      <p className="text-caption text-fg-secondary">{t('devotionals.bodyLabel')}</p>
                      <p className="mt-1 whitespace-pre-wrap text-body-sm">{detail.body}</p>
                    </div>
                    {detail.reflectionQuestion ? (
                      <div>
                        <p className="text-caption text-fg-secondary">
                          {t('devotionals.reflectionLabel')}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-body-sm">
                          {detail.reflectionQuestion}
                        </p>
                      </div>
                    ) : null}
                    {detail.prayer ? (
                      <div>
                        <p className="text-caption text-fg-secondary">{t('devotionals.prayerLabel')}</p>
                        <p className="mt-1 whitespace-pre-wrap text-body-sm">{detail.prayer}</p>
                      </div>
                    ) : null}
                    {detail.audioUrl ? (
                      <div>
                        <p className="text-caption text-fg-secondary">{t('devotionals.audioLabel')}</p>
                        <audio controls src={detail.audioUrl} className="mt-2 w-full" preload="metadata">
                          <track kind="captions" />
                        </audio>
                      </div>
                    ) : null}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                {detail.status === 'published' ? (
                  <Button
                    variant="outline"
                    loading={unpublish.isPending}
                    onClick={() => setUnpublishTarget(detail)}
                  >
                    {t('devotionals.unpublish')}
                  </Button>
                ) : null}
                {detailShowPublish ? (
                  <Button variant="outline" onClick={() => setPublishTarget(detail)}>
                    {t('devotionals.publish')}
                  </Button>
                ) : null}
                {detail.canDelete === true ? (
                  <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                    <Button variant="outline" onClick={() => setDeleteTarget(detail)}>
                      {tCommon('action.delete')}
                    </Button>
                  </PermissionGate>
                ) : null}
                <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                  <Button onClick={() => openEdit(detail)}>{tCommon('action.edit')}</Button>
                </PermissionGate>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t('confirm.publishDevotional.title', { name: publishTarget?.title ?? '' })}
        description={t('confirm.publishDevotional.description')}
        confirmLabel={t('confirm.publishDevotional.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnpublishTarget(null);
        }}
        title={t('confirm.unpublishDevotional.title', { name: unpublishTarget?.title ?? '' })}
        description={t('confirm.unpublishDevotional.description')}
        confirmLabel={t('confirm.unpublishDevotional.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('confirm.deleteDevotional.title', { name: deleteTarget?.title ?? '' })}
        description={t('confirm.deleteDevotional.description')}
        confirmLabel={t('confirm.deleteDevotional.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog
        open={formOpen}
        onOpenChange={(next) => {
          setFormOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{editing ? t('devotionals.edit') : t('devotionals.create')}</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <div>
                <Label htmlFor="devotional-title">{t('devotionals.titleLabel')}</Label>
                <Input
                  id="devotional-title"
                  value={form.title}
                  onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="devotional-date">{t('devotionals.dateLabel')}</Label>
                <DatePicker
                  id="devotional-date"
                  value={form.devotionalDate}
                  onChange={(date) =>
                    setForm((current) => ({ ...current, devotionalDate: date }))
                  }
                  popoverPortal="dialog"
                  className="max-w-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t('devotionals.scriptureLabel')}</Label>
                <ScriptureRefPicker
                  id="devotional-scripture"
                  value={form.scriptureRef}
                  onChange={(scriptureRef) =>
                    setForm((current) => ({ ...current, scriptureRef }))
                  }
                  allowRange
                  popoverPortal="dialog"
                  labels={scriptureRefPickerLabels(t, tCommon('action.clearSelection'))}
                />
              </div>

              <div>
                <Label htmlFor="devotional-body">{t('devotionals.bodyLabel')}</Label>
                <Textarea
                  id="devotional-body"
                  value={form.body}
                  onChange={(e) => setForm((current) => ({ ...current, body: e.target.value }))}
                  rows={5}
                  placeholder={t('devotionals.bodyPlaceholder')}
                />
              </div>

              <div>
                <Label htmlFor="devotional-reflection">{t('devotionals.reflectionLabel')}</Label>
                <Textarea
                  id="devotional-reflection"
                  value={form.reflectionQuestion}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, reflectionQuestion: e.target.value }))
                  }
                  rows={2}
                  placeholder={t('devotionals.reflectionPlaceholder')}
                />
              </div>

              <div>
                <Label htmlFor="devotional-prayer">{t('devotionals.prayerLabel')}</Label>
                <Textarea
                  id="devotional-prayer"
                  value={form.prayer}
                  onChange={(e) => setForm((current) => ({ ...current, prayer: e.target.value }))}
                  rows={3}
                  placeholder={t('devotionals.prayerPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('devotionals.artworkLabel')}</Label>
                <Tip tipId="content.devotional.artwork" className="mb-2" />
                <ChurchLogoUploader
                  layout="inline"
                  src={form.imagePreviewUrl}
                  name={form.title || t('devotionals.title')}
                  onUpload={async (blob) => {
                    const { key } = await uploadBlob({ purpose: 'content_image', blob });
                    setForm((current) => ({
                      ...current,
                      imageKey: key,
                      imagePreviewUrl: URL.createObjectURL(blob),
                    }));
                  }}
                  onRemove={
                    form.imageKey
                      ? () =>
                          setForm((current) => ({
                            ...current,
                            imageKey: null,
                            imagePreviewUrl: null,
                          }))
                      : undefined
                  }
                  labels={{
                    change: t('devotionals.artworkChange'),
                    remove: t('devotionals.artworkRemove'),
                    cropTitle: t('devotionals.artworkCropTitle'),
                    zoom: tCommon('media.zoom'),
                    apply: t('devotionals.artworkApply'),
                    cancel: tCommon('action.cancel'),
                    instructions: t('devotionals.artworkInstructions'),
                    closeCropper: tCommon('action.close'),
                  }}
                />
              </div>

              <div>
                <Label>{t('devotionals.audioLabel')}</Label>
                <Tip tipId="content.devotional.audio" className="mb-2" />
                <FileDropzone
                  accept="audio/mpeg,audio/mp4,audio/aac,audio/ogg,.mp3,.m4a,.aac,.ogg"
                  acceptLabel={t('devotionals.audioAccept')}
                  maxBytes={25 * 1024 * 1024}
                  maxSizeLabel={t('devotionals.audioMaxSize')}
                  disabled={audioUploading || save.isPending}
                  files={form.audioName ? [{ name: form.audioName }] : undefined}
                  onRemove={() =>
                    setForm((current) => ({
                      ...current,
                      audioKey: null,
                      audioName: null,
                      audioDurationSeconds: null,
                    }))
                  }
                  onFiles={(files) => {
                    const file = files[0];
                    if (!file) return;
                    void (async () => {
                      setAudioUploading(true);
                      try {
                        const duration = await audioDurationSeconds(file);
                        const { key } = await uploadBlob({
                          purpose: 'sermon_audio',
                          blob: file,
                          fileName: file.name,
                        });
                        setForm((current) => ({
                          ...current,
                          audioKey: key,
                          audioName: file.name,
                          audioDurationSeconds: duration,
                        }));
                      } catch (err) {
                        toast.error(
                          err instanceof ApiError
                            ? tErrors(err.code, err.params)
                            : tErrors('UNEXPECTED'),
                        );
                      } finally {
                        setAudioUploading(false);
                      }
                    })();
                  }}
                  labels={{
                    prompt: t('devotionals.audioPrompt'),
                    remove: t('devotionals.audioRemove'),
                  }}
                />
              </div>

              <SwitchField
                id="devotional-notify"
                label={t('devotionals.notifyLabel')}
                tipId="content.devotional.notify"
                checked={form.notifyOnPublish}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, notifyOnPublish: checked }))
                }
              />

              {editingPublished ? null : (
              <div>
                <Label>{t('devotionals.publishAtLabel')}</Label>
                <Tip tipId="content.devotional.schedule" className="mb-2" />
                <DateTimePicker
                  value={form.scheduledFor}
                  onChange={(scheduledFor) =>
                    setForm((current) => ({ ...current, scheduledFor }))
                  }
                  invalid={scheduleInPast}
                  popoverPortal="dialog"
                  timezoneNotice={(timezone) => t('devotionals.timezoneNotice', { timezone })}
                />
                {scheduleInPast ? (
                  <p className="mt-1 text-caption text-danger">{t('devotionals.publishAtFuture')}</p>
                ) : null}
              </div>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            {editingPublished ? (
              <Button
                disabled={!canSubmit}
                loading={pendingAction === 'draft' && save.isPending}
                onClick={() => submit('draft')}
              >
                {tCommon('action.save')}
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={!canSaveDraft}
                  loading={pendingAction === 'draft' && save.isPending}
                  onClick={() => submit('draft')}
                >
                  {t('devotionals.saveDraft')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={!canSchedule}
                  loading={pendingAction === 'schedule' && save.isPending}
                  onClick={() => submit('schedule')}
                >
                  {t('devotionals.schedulePublish')}
                </Button>
                <Button
                  disabled={!canPublish}
                  loading={pendingAction === 'publish' && save.isPending}
                  onClick={() => submit('publish')}
                >
                  {t('devotionals.publish')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
